/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * HeaderFooterEditor — modal to configure page header/nav and footer.
 * Header tab: full MenuNavPanel (saved to page_config.header).
 * Footer tab: comprehensive layout, content, style, and responsive controls.
 * Saves page_config via PATCH /arcee/application/page/<pageId>/update/
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { usePages, updatePage } from '../../hooks/usePages';
import { MenuNavPanel, DEFAULT_MENU_CONFIG, DEFAULT_ITEM } from './editors/MenuEditor';

// ─── Constants ────────────────────────────────────────────────────────────────

const SOCIAL_PLATFORMS = [
  { value: 'twitter',   label: 'Twitter / X',  icon: 'bi-twitter-x' },
  { value: 'facebook',  label: 'Facebook',      icon: 'bi-facebook' },
  { value: 'instagram', label: 'Instagram',     icon: 'bi-instagram' },
  { value: 'linkedin',  label: 'LinkedIn',      icon: 'bi-linkedin' },
  { value: 'youtube',   label: 'YouTube',       icon: 'bi-youtube' },
  { value: 'github',    label: 'GitHub',        icon: 'bi-github' },
  { value: 'tiktok',    label: 'TikTok',        icon: 'bi-tiktok' },
  { value: 'discord',   label: 'Discord',       icon: 'bi-discord' },
  { value: 'whatsapp',  label: 'WhatsApp',      icon: 'bi-whatsapp' },
  { value: 'email',     label: 'Email',         icon: 'bi-envelope-fill' },
  { value: 'phone',     label: 'Phone',         icon: 'bi-telephone-fill' },
  { value: 'website',   label: 'Website',       icon: 'bi-globe2' },
];

const FONT_WEIGHTS = [
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi-Bold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra Bold' },
];

const TABS = [
  { key: 'header', label: 'Header / Nav', icon: 'bi-compass' },
  { key: 'footer', label: 'Footer',       icon: 'bi-layout-text-window-reverse' },
];

const DEFAULT_FOOTER = {
  enabled:              false,
  mode:                 'default',
  layout:               'columns',
  content_alignment:    'left',
  columns:              [],
  brand_name:           '',
  brand_logo_url:       '',
  tagline:              '',
  social_links:         [],
  social_icon_size:     18,
  bottom_bar_enabled:   true,
  copyright_text:       '',
  bottom_bar_links:     [],
  bottom_bar_alignment: 'center',
  bg_color:             '#1e293b',
  text_color:           '#94a3b8',
  title_color:          '#ffffff',
  link_color:           '#cbd5e1',
  bottom_bar_bg:        '',
  bottom_bar_text_color:'',
  font_size:            14,
  title_font_size:      13,
  font_weight:          '400',
  title_font_weight:    '700',
  padding_top:          48,
  padding_bottom:       32,
  column_gap:           32,
  row_gap:              10,
  min_height:           0,
  border_top_enabled:   true,
  border_color:         'rgba(255,255,255,0.1)',
  border_width:         1,
  box_shadow:           'none',
  mobile_alignment:     'center',
};

// ─── Footer migration ─────────────────────────────────────────────────────────

// Footers saved before the mode selector existed have no `mode` key. Anything
// the author actually configured must keep rendering as designed, so those fall
// back to 'custom'; an enabled-but-empty footer (which rendered as a bare strip)
// becomes 'default'. Mirrors resolveFooterMode() in the client's DynamicFooter.
function resolveFooterMode(cfg) {
  if (!cfg) return 'default';
  if (cfg.mode === 'default' || cfg.mode === 'custom') return cfg.mode;
  const hasCustomContent =
    (cfg.columns || []).length > 0 ||
    (cfg.social_links || []).length > 0 ||
    (cfg.bottom_bar_links || []).length > 0 ||
    !!cfg.brand_name ||
    !!cfg.brand_logo_url ||
    !!cfg.tagline ||
    !!cfg.copyright_text;
  return hasCustomContent ? 'custom' : 'default';
}

// ─── Header migration ─────────────────────────────────────────────────────────

function migrateHeaderConfig(raw) {
  if (!raw) return { ...DEFAULT_MENU_CONFIG };
  if (Array.isArray(raw.items)) return { ...DEFAULT_MENU_CONFIG, ...raw };
  const base = { ...DEFAULT_MENU_CONFIG };
  if (raw.logo_url)  { base.logoImageUrl = raw.logo_url; base.logoType = 'both'; }
  if (raw.app_name)  { base.logoText = raw.app_name; }
  if (Array.isArray(raw.nav_links) && raw.nav_links.length > 0) {
    base.items = raw.nav_links.map((l, i) => ({
      ...DEFAULT_ITEM, itemId: `migrated_${i}`,
      label: l.label || 'Link', navType: 'url', url: l.url || '#',
      openInNewTab: l.target === '_blank',
    }));
  }
  if (raw.show_sign_in !== false || raw.show_sign_up !== false) {
    base.navCtaVisible = raw.show_sign_in !== false;
    base.navCtaLabel   = raw.sign_in_label || 'Sign In';
    base.navCtaNavType = 'url';
    base.navCtaTarget  = raw.sign_in_url || '/sign-in';
  }
  return base;
}

// ─── Small UI helpers ─────────────────────────────────────────────────────────

function Acc({ label, icon, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9', marginBottom: 2 }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer',
        color: '#1e293b', fontWeight: 600, fontSize: 12, textAlign: 'left',
      }}>
        {icon && <i className={`bi ${icon}`} style={{ color: '#64748b', fontSize: 13 }} />}
        <span style={{ flex: 1 }}>{label}</span>
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
      </button>
      {open && <div style={{ paddingBottom: 14 }}>{children}</div>}
    </div>
  );
}

function IRow({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      {label && <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 3 }}>{label}</div>}
      {hint  && <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>{hint}</div>}
      {children}
    </div>
  );
}

function InlineRow({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
      <span style={{ fontSize: 11, color: '#374151', flexShrink: 0 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={() => onChange(!value)} style={{
      width: 34, height: 18, borderRadius: 9, cursor: 'pointer', flexShrink: 0,
      background: value ? 'var(--primary-color)' : '#e2e8f0', position: 'relative', transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute', top: 2, left: value ? 17 : 2,
        width: 14, height: 14, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function NumInput({ value, onChange, min = 0, max = 999, suffix = 'px' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <input type="number" min={min} max={max} value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: 64, fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, textAlign: 'right' }} />
      {suffix && <span style={{ fontSize: 11, color: '#94a3b8' }}>{suffix}</span>}
    </div>
  );
}

function ColorRow({ label, field, footer, setFooter, placeholder = '#000000' }) {
  return (
    <div className="col-6" style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="color"
          value={footer[field] || placeholder}
          onChange={e => setFooter(f => ({ ...f, [field]: e.target.value }))}
          style={{ width: 32, height: 26, padding: 2, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }} />
        <input
          value={footer[field] || ''}
          onChange={e => setFooter(f => ({ ...f, [field]: e.target.value }))}
          placeholder={placeholder}
          style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontFamily: 'monospace', minWidth: 0 }} />
        {footer[field] && (
          <button type="button" onClick={() => setFooter(f => ({ ...f, [field]: '' }))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 2px', fontSize: 12, flexShrink: 0 }}>
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

function AlignPicker({ value, onChange }) {
  return (
    <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', width: 'fit-content' }}>
      {[
        { v: 'left',   icon: 'bi-text-left' },
        { v: 'center', icon: 'bi-text-center' },
        { v: 'right',  icon: 'bi-text-right' },
      ].map(o => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} style={{
          padding: '5px 11px', border: 'none',
          background: value === o.v ? 'var(--primary-color)' : '#fff',
          color: value === o.v ? '#fff' : '#64748b',
          cursor: 'pointer', fontSize: 13,
        }}>
          <i className={`bi ${o.icon}`} />
        </button>
      ))}
    </div>
  );
}

// ─── Footer mode picker ───────────────────────────────────────────────────────

const FOOTER_MODES = [
  { value: 'default', label: 'Default', icon: 'bi-stars',    hint: 'Use the built-in Arcee footer' },
  { value: 'custom',  label: 'Custom',  icon: 'bi-sliders',  hint: 'Design your own footer' },
];

function FooterModePicker({ value, onChange, theme }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
      {FOOTER_MODES.map(m => {
        const active = value === m.value;
        return (
          <button
            key={m.value}
            type="button"
            onClick={() => onChange(m.value)}
            style={{
              flex: 1,
              textAlign: 'left',
              padding: '10px 12px',
              borderRadius: 8,
              cursor: 'pointer',
              background: active ? `${theme.primary}0f` : '#fff',
              border: `1.5px solid ${active ? theme.primary : '#e2e8f0'}`,
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className={`bi ${m.icon}`} style={{ fontSize: 13, color: active ? theme.primary : '#94a3b8' }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: active ? theme.primary : '#475569' }}>{m.label}</span>
              {active && <i className="bi bi-check-circle-fill" style={{ fontSize: 11, color: theme.primary, marginLeft: 'auto' }} />}
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{m.hint}</div>
          </button>
        );
      })}
    </div>
  );
}

// ─── ID generators ────────────────────────────────────────────────────────────

const uid = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

// ─── Column editor sub-component ──────────────────────────────────────────────

function ColumnEditor({ col, onUpdate, onRemove }) {
  const [open, setOpen] = useState(true);

  const addItem = (type) => {
    const item = type === 'link'
      ? { id: uid('itm'), type: 'link', label: '', url: '', icon: '', open_new_tab: false }
      : { id: uid('itm'), type: 'text', text: '' };
    onUpdate({ items: [...(col.items || []), item] });
  };

  const updateItem = (itemId, patch) =>
    onUpdate({ items: col.items.map(i => i.id === itemId ? { ...i, ...patch } : i) });

  const removeItem = (itemId) =>
    onUpdate({ items: col.items.filter(i => i.id !== itemId) });

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8, background: '#fafafa', overflow: 'hidden' }}>
      {/* Column header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', background: '#f1f5f9' }}>
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0, fontSize: 12, flexShrink: 0 }}>
          <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
        </button>
        <input
          value={col.title || ''}
          onChange={e => onUpdate({ title: e.target.value })}
          placeholder="Column title (optional)"
          style={{ flex: 1, fontSize: 12, fontWeight: 600, border: 'none', background: 'transparent', outline: 'none', color: '#1e293b' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <Toggle value={col.title_visible !== false} onChange={v => onUpdate({ title_visible: v })} />
          <span style={{ fontSize: 10, color: '#94a3b8' }}>Show title</span>
        </div>
        <button type="button" onClick={onRemove}
          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 4, padding: '2px 7px', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>
          <i className="bi bi-trash" />
        </button>
      </div>

      {open && (
        <div style={{ padding: '10px 10px 6px' }}>
          {(col.items || []).map(item => (
            <div key={item.id} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 6, padding: '6px 8px', background: '#fff', borderRadius: 6, border: '1px solid #e2e8f0' }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {item.type === 'link' ? (
                  <>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input value={item.label || ''} onChange={e => updateItem(item.id, { label: e.target.value })}
                        placeholder="Label" style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
                      <input value={item.url || ''} onChange={e => updateItem(item.id, { url: e.target.value })}
                        placeholder="URL" style={{ flex: 2, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input value={item.icon || ''} onChange={e => updateItem(item.id, { icon: e.target.value })}
                        placeholder="Icon (e.g. bi-arrow-right)" style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#64748b', cursor: 'pointer', flexShrink: 0 }}>
                        <input type="checkbox" checked={!!item.open_new_tab}
                          onChange={e => updateItem(item.id, { open_new_tab: e.target.checked })} />
                        New tab
                      </label>
                    </div>
                  </>
                ) : (
                  <input value={item.text || item.label || ''} onChange={e => updateItem(item.id, { text: e.target.value, label: e.target.value })}
                    placeholder="Text content" style={{ width: '100%', fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                <span style={{
                  fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
                  background: item.type === 'link' ? '#e0f2fe' : '#f0fdf4',
                  color:      item.type === 'link' ? '#0369a1' : '#166534',
                }}>
                  {item.type}
                </span>
                <button type="button" onClick={() => removeItem(item.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px 4px', fontSize: 12 }}>
                  <i className="bi bi-x" />
                </button>
              </div>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button type="button" onClick={() => addItem('link')}
              style={{ flex: 1, border: '1px dashed #cbd5e1', borderRadius: 5, background: 'none', color: '#64748b', fontSize: 11, padding: '5px 0', cursor: 'pointer' }}>
              <i className="bi bi-link-45deg me-1" />Add Link
            </button>
            <button type="button" onClick={() => addItem('text')}
              style={{ flex: 1, border: '1px dashed #cbd5e1', borderRadius: 5, background: 'none', color: '#64748b', fontSize: 11, padding: '5px 0', cursor: 'pointer' }}>
              <i className="bi bi-type me-1" />Add Text
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Preview URL ──────────────────────────────────────────────────────────────

const HF_PREVIEW_URL = process.env.REACT_APP_PUBLIC_CLIENT_URL
  ? `${process.env.REACT_APP_PUBLIC_CLIENT_URL}/p/hf-preview`
  : null;

// ─── Main component ───────────────────────────────────────────────────────────

export default function HeaderFooterEditor({ pageId, appId, allSections, initialPageConfig, onClose, onSaved }) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('header');
  const [saving, setSaving]       = useState(false);
  const [footerTab, setFooterTab] = useState('layout');
  const [iframeReady, setIframeReady]         = useState(false);
  const [configCollapsed, setConfigCollapsed] = useState(false);
  const [leftPxWidth, setLeftPxWidth]         = useState(null); // null = 45% default
  const [previewScale, setPreviewScale]       = useState(1);
  const [previewPaneH, setPreviewPaneH]       = useState(600);
  const iframeRef        = useRef(null);
  const previewPaneRef   = useRef(null);
  const splitContainerRef = useRef(null);
  const isDragging       = useRef(false);
  const dragStartX       = useRef(0);
  const dragStartLeftW   = useRef(0);
  const PREVIEW_DESIGN_WIDTH = 1280;

  const init = initialPageConfig || {};

  // ── Header state ──────────────────────────────────────────────────────────
  // show_header lives on page_config (not inside header) so a page can suppress
  // the default header while keeping its configured menu for later. Defaults to
  // true so existing pages keep rendering their header unchanged.
  const [showHeader, setShowHeader] = useState(init.show_header !== false);
  // use_app_header also lives on page_config. When on, the client portal shows
  // its own brand bar — logo and app name only — in place of the menu
  // configured here. The configured menu is kept, not cleared, so turning this
  // back off restores it untouched. Defaults to false so every existing page
  // renders exactly as before.
  const [useAppHeader, setUseAppHeader] = useState(init.use_app_header === true);
  const [header, setHeader] = useState(() => migrateHeaderConfig(init.header));
  const set      = useCallback((patch)     => setHeader(h => ({ ...h, ...patch })), []);
  const setItems = useCallback((newItems)  => setHeader(h => ({ ...h, items: newItems })), []);

  const { data: pagesData } = usePages(appId);
  const workspacePages = pagesData?.pages || pagesData || [];

  // ── Footer state ──────────────────────────────────────────────────────────
  const [footer, setFooter] = useState(() => {
    const saved = init.footer || {};
    return { ...DEFAULT_FOOTER, ...saved, mode: resolveFooterMode(init.footer) };
  });

  // Column helpers
  const addColumn    = () => setFooter(f => ({ ...f, columns: [...f.columns, { id: uid('col'), title: '', title_visible: true, items: [] }] }));
  const removeColumn = (id) => setFooter(f => ({ ...f, columns: f.columns.filter(c => c.id !== id) }));
  const updateColumn = (id, patch) => setFooter(f => ({ ...f, columns: f.columns.map(c => c.id === id ? { ...c, ...patch } : c) }));

  // Social link helpers
  const addSocial    = () => setFooter(f => ({ ...f, social_links: [...f.social_links, { platform: 'twitter', url: '' }] }));
  const removeSocial = (i) => setFooter(f => ({ ...f, social_links: f.social_links.filter((_, idx) => idx !== i) }));
  const updateSocial = (i, patch) => setFooter(f => ({ ...f, social_links: f.social_links.map((s, idx) => idx === i ? { ...s, ...patch } : s) }));

  // Bottom bar link helpers
  const addBarLink    = () => setFooter(f => ({ ...f, bottom_bar_links: [...(f.bottom_bar_links || []), { label: '', url: '' }] }));
  const removeBarLink = (i) => setFooter(f => ({ ...f, bottom_bar_links: (f.bottom_bar_links || []).filter((_, idx) => idx !== i) }));
  const updateBarLink = (i, patch) => setFooter(f => ({ ...f, bottom_bar_links: (f.bottom_bar_links || []).map((l, idx) => idx === i ? { ...l, ...patch } : l) }));

  // ── iframe preview: track pane size for scale calculation ───────────────────
  useEffect(() => {
    const el = previewPaneRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setPreviewScale(width / PREVIEW_DESIGN_WIDTH);
      setPreviewPaneH(height);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [PREVIEW_DESIGN_WIDTH]);

  // ── Drag-to-resize divider ────────────────────────────────────────────────
  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartLeftW.current =
      previewPaneRef.current?.offsetWidth ||
      (splitContainerRef.current?.offsetWidth * 0.45) || 400;
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const containerW = splitContainerRef.current?.offsetWidth || window.innerWidth;
      const delta = e.clientX - dragStartX.current;
      const newW = Math.max(260, Math.min(containerW - 300, dragStartLeftW.current + delta));
      setLeftPxWidth(newW);
    };
    const onUp = () => { isDragging.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  // ── iframe preview: listen for ready signal ───────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'ARCEE_HF_READY') setIframeReady(true);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // ── iframe preview: push config on every change ───────────────────────────
  const sendToIframe = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;
    iframeRef.current.contentWindow.postMessage(
      { type: 'ARCEE_HF_SCHEMA', header, footer, theme, show_header: showHeader, use_app_header: useAppHeader },
      '*'
    );
  }, [header, footer, theme, showHeader, useAppHeader]);

  useEffect(() => {
    if (iframeReady) sendToIframe();
  }, [iframeReady, sendToIframe]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const page_config = { header, footer, show_header: showHeader, use_app_header: useAppHeader };
      await updatePage(pageId, { page_config });
      onSaved(page_config);
    } catch (err) {
      alert(err.message || 'Failed to save header/footer settings.');
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', flexDirection: 'column', background: '#f1f5f9' }}>

      {/* ── Gradient header (matches PropertiesDrawer) ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '0 20px', height: 62, flexShrink: 0,
        background: theme.gradientBackground,
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
      }}>
        <button type="button" onClick={onClose} style={{
          background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600,
          padding: '5px 12px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        }}>
          <i className="bi bi-arrow-left" style={{ fontSize: 13 }} />
          Back
        </button>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Page Settings
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginTop: 1 }}>
            Header &amp; Footer
          </div>
        </div>

        <button type="button" onClick={handleSave} disabled={saving} style={{
          background: saving ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.18)',
          border: '1px solid rgba(255,255,255,0.28)',
          borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600,
          padding: '5px 16px', cursor: saving ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        }}>
          {saving
            ? <><span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13 }} /> Saving…</>
            : <><i className="bi bi-floppy" style={{ fontSize: 13 }} /> Save</>
          }
        </button>
      </div>

      {/* ── Body: split pane ── */}
      <div ref={splitContainerRef} style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Left pane: Live preview ── */}
        <div
          ref={previewPaneRef}
          style={{
            width: configCollapsed
              ? '100%'
              : (leftPxWidth ? `${leftPxWidth}px` : '45%'),
            flexShrink: 0,
            background: '#e8edf4',
            overflow: 'hidden',
            position: 'relative',
            transition: configCollapsed ? 'width 0.2s ease' : undefined,
          }}
        >
          {/* Expand / Show Config toggle */}
          <button
            type="button"
            onClick={() => setConfigCollapsed(c => !c)}
            style={{
              position: 'absolute', top: '50%', right: 0, zIndex: 10,
              transform: 'translateY(-50%)',
              background: theme.primary,
              border: 'none',
              borderRadius: '6px 0 0 6px',
              padding: '10px 5px', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              boxShadow: '-2px 0 8px rgba(0,0,0,0.12)',
              writingMode: 'vertical-rl',
            }}
            title={configCollapsed ? 'Show configuration panel' : 'Expand preview to full width'}
          >
            <i
              className={`bi ${configCollapsed ? 'bi-chevron-left' : 'bi-chevron-right'}`}
              style={{ fontSize: 11, color: '#fff', writingMode: 'horizontal-tb' }}
            />
            <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', letterSpacing: '0.06em', writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              {configCollapsed ? 'Config' : 'Expand'}
            </span>
          </button>

          {!HF_PREVIEW_URL ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 12, padding: 24, textAlign: 'center' }}>
              <div>
                <i className="bi bi-eye-slash" style={{ fontSize: 28, display: 'block', marginBottom: 8 }} />
                Set <code>REACT_APP_PUBLIC_CLIENT_URL</code> in <code>.env.local</code> to enable live preview.
              </div>
            </div>
          ) : (
            <>
              {!iframeReady && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e8edf4', zIndex: 1 }}>
                  <span className="spinner-border spinner-border-sm" style={{ color: '#94a3b8' }} />
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={HF_PREVIEW_URL}
                title="Header / Footer Preview"
                style={{
                  position: 'absolute',
                  top: 0, left: 0,
                  width: `${PREVIEW_DESIGN_WIDTH}px`,
                  height: previewScale > 0 ? `${Math.ceil(previewPaneH / previewScale)}px` : '100%',
                  border: 'none',
                  transform: `scale(${previewScale})`,
                  transformOrigin: 'top left',
                  opacity: iframeReady ? 1 : 0,
                }}
              />
            </>
          )}
        </div>

        {/* ── Drag divider ── */}
        {!configCollapsed && (
          <div
            onMouseDown={handleDividerMouseDown}
            style={{
              width: 6, flexShrink: 0,
              background: '#e2e8f0',
              cursor: 'col-resize',
              position: 'relative',
              userSelect: 'none',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = theme.primary + '55'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#e2e8f0'; }}
          >
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex', flexDirection: 'column', gap: 3, pointerEvents: 'none',
            }}>
              {[0,1,2,3,4].map(i => (
                <div key={i} style={{ width: 2, height: 2, borderRadius: '50%', background: '#94a3b8' }} />
              ))}
            </div>
          </div>
        )}

        {/* ── Right pane: Config ── */}
        {!configCollapsed && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', flexShrink: 0, background: '#f8fafc' }}>
            {TABS.map(tab => (
              <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} style={{
                flex: 1, padding: '10px 0', border: 'none',
                borderBottom: activeTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
                background: 'transparent',
                color: activeTab === tab.key ? theme.primary : '#64748b',
                fontWeight: activeTab === tab.key ? 700 : 400,
                fontSize: 12, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}>
                <i className={`bi ${tab.icon}`} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Scrollable config content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>

            {/* ── Header / Nav tab ───────────────────────────────────────── */}
            {activeTab === 'header' && (
              <div style={{ paddingTop: 12, paddingBottom: 12 }}>

                {/* Include toggle */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', background: '#f8fafc', borderRadius: 8,
                  border: '1px solid #e2e8f0', marginBottom: 14,
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Include Header</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                      When disabled, no header is shown on this page. Turn this off when your
                      page content already provides its own navigation.
                    </div>
                  </div>
                  <Toggle value={showHeader} onChange={setShowHeader} />
                </div>

                {/* Swap the configured menu for the portal's brand bar */}
                {showHeader && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', background: '#f8fafc', borderRadius: 8,
                    border: '1px solid #e2e8f0', marginBottom: 14,
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Logo and app name</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                        Shows the site&apos;s logo and name instead of the menu below.
                      </div>
                    </div>
                    <Toggle value={useAppHeader} onChange={setUseAppHeader} />
                  </div>
                )}

                {showHeader && !useAppHeader && (
                  <>
                    <div className="alert alert-info py-2 small mb-3">
                      <i className="bi bi-info-circle me-1" />
                      Configure your site navigation — logo, menu items, behaviour, styling, and mobile menu.
                    </div>
                    <MenuNavPanel cfg={header} set={set} setItems={setItems}
                      allSections={allSections || []} workspacePages={workspacePages} theme={theme} />
                  </>
                )}
              </div>
            )}

            {/* ── Footer tab ─────────────────────────────────────────────── */}
            {activeTab === 'footer' && (
              <div style={{ paddingTop: 12, paddingBottom: 24 }}>

                {/* Enable toggle */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', background: '#f8fafc', borderRadius: 8,
                  border: '1px solid #e2e8f0', marginBottom: 14,
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>Enable Footer</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                      When disabled, no footer is shown on this page.
                    </div>
                  </div>
                  <Toggle value={footer.enabled} onChange={v => setFooter(f => ({ ...f, enabled: v }))} />
                </div>

                {/* Mode: built-in Arcee footer vs. a footer the author designs */}
                {footer.enabled && (
                  <FooterModePicker
                    value={footer.mode}
                    onChange={v => setFooter(f => ({ ...f, mode: v }))}
                    theme={theme}
                  />
                )}

                {/* Default mode — nothing to configure, so explain what ships */}
                {footer.enabled && footer.mode === 'default' && (
                  <div style={{
                    padding: '12px 14px', background: '#f8fafc',
                    border: '1px dashed #cbd5e1', borderRadius: 8,
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                      <i className="bi bi-info-circle me-1" style={{ color: '#94a3b8' }} />
                      Arcee default footer
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.7 }}>
                      This page shows the built-in footer:
                      <ul style={{ paddingLeft: 18, margin: '6px 0 0' }}>
                        <li>“Powered by <strong>arcee.io</strong>” — opens arcee.io in a new tab</li>
                        <li>“Developed by <strong>RUDRACORESOFT</strong>” — opens rudracoresoft.com in a new tab</li>
                        <li>Rudra Coresoft copyright line</li>
                      </ul>
                      <div style={{ marginTop: 8 }}>
                        Switch to <strong>Custom</strong> to build your own columns, links, social icons and styling.
                      </div>
                    </div>
                  </div>
                )}

                {footer.enabled && footer.mode === 'custom' && (
                  <>
                    {/* ── Footer section tab bar ── */}
                    <div style={{
                      display: 'flex',
                      overflowX: 'auto',
                      borderBottom: '2px solid #e2e8f0',
                      marginTop: 14,
                      marginLeft: -20,
                      marginRight: -20,
                      paddingLeft: 20,
                      background: '#f8fafc',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                    }}>
                      {[
                        { key: 'layout',     label: 'Layout',     icon: 'bi-layout-three-columns',    badge: footer.columns.length > 0 ? `${footer.columns.length}` : null },
                        { key: 'social',     label: 'Social',     icon: 'bi-share',                  badge: (footer.social_links || []).length > 0 ? `${footer.social_links.length}` : null },
                        { key: 'bottombar',  label: 'Bottom Bar', icon: 'bi-distribute-horizontal',  badge: null },
                        { key: 'colors',     label: 'Colors',     icon: 'bi-palette',                badge: null },
                        { key: 'typography', label: 'Typography', icon: 'bi-type',                   badge: null },
                        { key: 'spacing',    label: 'Spacing',    icon: 'bi-arrows-expand',          badge: null },
                        { key: 'border',     label: 'Border',     icon: 'bi-border-style',           badge: null },
                      ].map(tab => (
                        <button key={tab.key} type="button" onClick={() => setFooterTab(tab.key)} style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '9px 14px',
                          border: 'none',
                          borderBottom: footerTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
                          marginBottom: -2,
                          background: 'transparent',
                          color: footerTab === tab.key ? theme.primary : '#64748b',
                          fontWeight: footerTab === tab.key ? 700 : 400,
                          fontSize: 11,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          transition: 'color 0.15s',
                        }}>
                          <i className={`bi ${tab.icon}`} style={{ fontSize: 12 }} />
                          {tab.label}
                          {tab.badge && (
                            <span style={{
                              fontSize: 9, fontWeight: 700,
                              background: footerTab === tab.key ? theme.primary : '#e2e8f0',
                              color: footerTab === tab.key ? '#fff' : '#64748b',
                              borderRadius: 10, padding: '1px 5px', marginLeft: 3,
                            }}>
                              {tab.badge}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* ── Footer section tab content ── */}
                    <div style={{ paddingTop: 14 }}>

                      {/* Layout & Columns */}
                      {footerTab === 'layout' && (
                        <div>
                          <InlineRow label="Content alignment">
                            <AlignPicker value={footer.content_alignment} onChange={v => setFooter(f => ({ ...f, content_alignment: v }))} />
                          </InlineRow>
                          <div style={{ height: 1, background: '#f1f5f9', margin: '12px 0' }} />
                          {footer.columns.length === 0 && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10, fontStyle: 'italic' }}>
                              No columns yet. Add a column to build your footer layout.
                            </div>
                          )}
                          {footer.columns.map(col => (
                            <ColumnEditor
                              key={col.id}
                              col={col}
                              onUpdate={patch => updateColumn(col.id, patch)}
                              onRemove={() => removeColumn(col.id)}
                            />
                          ))}
                          {footer.columns.length < 4 && (
                            <button type="button" onClick={addColumn} style={{
                              width: '100%', border: '1.5px dashed #cbd5e1', borderRadius: 7,
                              background: 'none', color: 'var(--primary-color)', fontSize: 12, fontWeight: 500,
                              padding: '8px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}>
                              <i className="bi bi-plus-circle" />Add Column ({footer.columns.length}/4)
                            </button>
                          )}
                        </div>
                      )}

                      {/* Social Links */}
                      {footerTab === 'social' && (
                        <div>
                          <InlineRow label="Icon size">
                            <NumInput value={footer.social_icon_size} onChange={v => setFooter(f => ({ ...f, social_icon_size: v }))} min={12} max={36} />
                          </InlineRow>

                          {(footer.social_links || []).map((s, i) => (
                            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                              <select
                                value={s.platform}
                                onChange={e => updateSocial(i, { platform: e.target.value })}
                                style={{ width: 130, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                                {SOCIAL_PLATFORMS.map(p => (
                                  <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                              </select>
                              <input
                                value={s.url || ''}
                                onChange={e => updateSocial(i, { url: e.target.value })}
                                placeholder="https://..."
                                style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
                              <button type="button" onClick={() => removeSocial(i)}
                                style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>
                                <i className="bi bi-trash" />
                              </button>
                            </div>
                          ))}
                          <button type="button" onClick={addSocial}
                            style={{ width: '100%', border: '1.5px dashed #cbd5e1', borderRadius: 6, background: 'none', color: '#64748b', fontSize: 11, padding: '6px 0', cursor: 'pointer', marginTop: 4 }}>
                            <i className="bi bi-plus me-1" />Add Social Link
                          </button>
                        </div>
                      )}

                      {/* Bottom Bar */}
                      {footerTab === 'bottombar' && (
                        <div>
                          <InlineRow label="Enable bottom bar">
                            <Toggle value={footer.bottom_bar_enabled} onChange={v => setFooter(f => ({ ...f, bottom_bar_enabled: v }))} />
                          </InlineRow>

                          {footer.bottom_bar_enabled && (
                            <>
                              <IRow label="Copyright text">
                                <input className="form-control form-control-sm"
                                  value={footer.copyright_text || ''}
                                  onChange={e => setFooter(f => ({ ...f, copyright_text: e.target.value }))}
                                  placeholder="e.g. © 2025 Acme Corp. All Rights Reserved." />
                              </IRow>
                              <InlineRow label="Alignment">
                                <AlignPicker value={footer.bottom_bar_alignment} onChange={v => setFooter(f => ({ ...f, bottom_bar_alignment: v }))} />
                              </InlineRow>

                              <IRow label="Bottom bar links" hint="Small links shown alongside copyright (e.g. Privacy Policy, Terms)">
                                {(footer.bottom_bar_links || []).map((link, i) => (
                                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                                    <input value={link.label || ''} onChange={e => updateBarLink(i, { ...link, label: e.target.value })}
                                      placeholder="Label" style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
                                    <input value={link.url || ''} onChange={e => updateBarLink(i, { ...link, url: e.target.value })}
                                      placeholder="URL" style={{ flex: 2, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
                                    <button type="button" onClick={() => removeBarLink(i)}
                                      style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>
                                      <i className="bi bi-trash" />
                                    </button>
                                  </div>
                                ))}
                                <button type="button" onClick={addBarLink}
                                  style={{ width: '100%', border: '1.5px dashed #cbd5e1', borderRadius: 5, background: 'none', color: '#64748b', fontSize: 11, padding: '5px 0', cursor: 'pointer', marginTop: 2 }}>
                                  <i className="bi bi-plus me-1" />Add Link
                                </button>
                              </IRow>
                            </>
                          )}
                        </div>
                      )}

                      {/* Colors & Background */}
                      {footerTab === 'colors' && (
                        <div className="row g-0">
                          <ColorRow label="Background"         field="bg_color"              footer={footer} setFooter={setFooter} placeholder="#1e293b" />
                          <ColorRow label="Body text"          field="text_color"            footer={footer} setFooter={setFooter} placeholder="#94a3b8" />
                          <ColorRow label="Heading / title"    field="title_color"           footer={footer} setFooter={setFooter} placeholder="#ffffff" />
                          <ColorRow label="Links"              field="link_color"            footer={footer} setFooter={setFooter} placeholder="#cbd5e1" />
                          <ColorRow label="Bottom bar bg"      field="bottom_bar_bg"         footer={footer} setFooter={setFooter} placeholder="rgba(0,0,0,0.18)" />
                          <ColorRow label="Bottom bar text"    field="bottom_bar_text_color" footer={footer} setFooter={setFooter} placeholder="(same as body text)" />
                        </div>
                      )}

                      {/* Typography */}
                      {footerTab === 'typography' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <IRow label="Body font size">
                            <NumInput value={footer.font_size} onChange={v => setFooter(f => ({ ...f, font_size: v }))} min={10} max={24} />
                          </IRow>
                          <IRow label="Body font weight">
                            <select value={footer.font_weight}
                              onChange={e => setFooter(f => ({ ...f, font_weight: e.target.value }))}
                              style={{ width: '100%', fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                              {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                            </select>
                          </IRow>
                          <IRow label="Title font size">
                            <NumInput value={footer.title_font_size} onChange={v => setFooter(f => ({ ...f, title_font_size: v }))} min={10} max={24} />
                          </IRow>
                          <IRow label="Title font weight">
                            <select value={footer.title_font_weight}
                              onChange={e => setFooter(f => ({ ...f, title_font_weight: e.target.value }))}
                              style={{ width: '100%', fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                              {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                            </select>
                          </IRow>
                        </div>
                      )}

                      {/* Spacing */}
                      {footerTab === 'spacing' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <InlineRow label="Padding top">
                            <NumInput value={footer.padding_top} onChange={v => setFooter(f => ({ ...f, padding_top: v }))} max={200} />
                          </InlineRow>
                          <InlineRow label="Padding bottom">
                            <NumInput value={footer.padding_bottom} onChange={v => setFooter(f => ({ ...f, padding_bottom: v }))} max={200} />
                          </InlineRow>
                          <InlineRow label="Column gap">
                            <NumInput value={footer.column_gap} onChange={v => setFooter(f => ({ ...f, column_gap: v }))} max={120} />
                          </InlineRow>
                          <InlineRow label="Row gap">
                            <NumInput value={footer.row_gap} onChange={v => setFooter(f => ({ ...f, row_gap: v }))} max={60} />
                          </InlineRow>
                          <InlineRow label="Min height">
                            <NumInput value={footer.min_height} onChange={v => setFooter(f => ({ ...f, min_height: v }))} max={600} />
                          </InlineRow>
                        </div>
                      )}

                      {/* Border & Shadow */}
                      {footerTab === 'border' && (
                        <div>
                          <InlineRow label="Top border">
                            <Toggle value={footer.border_top_enabled} onChange={v => setFooter(f => ({ ...f, border_top_enabled: v }))} />
                          </InlineRow>
                          {footer.border_top_enabled && (
                            <>
                              <InlineRow label="Border width">
                                <NumInput value={footer.border_width} onChange={v => setFooter(f => ({ ...f, border_width: v }))} min={1} max={8} />
                              </InlineRow>
                              <IRow label="Border color">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <input type="color"
                                    value={footer.border_color?.startsWith('rgba') ? '#94a3b8' : (footer.border_color || '#e2e8f0')}
                                    onChange={e => setFooter(f => ({ ...f, border_color: e.target.value }))}
                                    style={{ width: 32, height: 26, padding: 2, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }} />
                                  <input value={footer.border_color || ''}
                                    onChange={e => setFooter(f => ({ ...f, border_color: e.target.value }))}
                                    placeholder="rgba(255,255,255,0.1)"
                                    style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontFamily: 'monospace' }} />
                                </div>
                              </IRow>
                            </>
                          )}
                          <IRow label="Box shadow">
                            <div style={{ display: 'flex', gap: 6 }}>
                              {[
                                { v: 'none', label: 'None' },
                                { v: 'sm',   label: 'Soft' },
                                { v: 'md',   label: 'Medium' },
                                { v: 'lg',   label: 'Strong' },
                              ].map(o => (
                                <button key={o.v} type="button" onClick={() => setFooter(f => ({ ...f, box_shadow: o.v }))} style={{
                                  flex: 1, padding: '5px 0', borderRadius: 6, cursor: 'pointer', fontSize: 11,
                                  border: footer.box_shadow === o.v ? '2px solid var(--primary-color)' : '1.5px solid #e2e8f0',
                                  background: footer.box_shadow === o.v ? '#f5f0ff' : '#fafafa',
                                  color: footer.box_shadow === o.v ? 'var(--primary-color)' : '#475569',
                                  fontWeight: footer.box_shadow === o.v ? 700 : 400,
                                }}>
                                  {o.label}
                                </button>
                              ))}
                            </div>
                          </IRow>
                        </div>
                      )}

                    </div>
                  </>
                )}
              </div>
            )}

          </div>
        </div>
        )}
      </div>
    </div>
  );
}
